import React, { useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, InputGroup, InputGroupText, Alert } from 'reactstrap';
import { parseCurrencyToFloat, normalizeFieldValue, displayDecimal } from '../../../../_helper/regexHandler';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';
import { ACTIONS_FOR_REPORT, EXPERT_REPORT_TYPES } from '../../../../_helper/schemaAllowedValues';

const ExpertWitnessReportDataModal = ({
    apiEndpoint,
    isOpen,
    toggle,
    mode,
    selectedExpertWitnessReport,
    refreshData,
    arbitrationId
}) => {
    const initialExpertWitnessReportData = {
        id: '',
        arbitration: [],
        arrivalDate: '',
        lastDay: '',
        reportType: '',
        ewDefectDeterminationFee: '',
        ewClaimDeterminationFee: '',
        actionForReport: '',
        amendmentFee: '',
        comments: ''
    };

    const customHandlers = useMemo(() => ({
        ewDefectDeterminationFee: (value, prev) => {
            const processed = normalizeFieldValue('ewDefectDeterminationFee', value);
            if (processed === '') return { ...prev, ewDefectDeterminationFee: '' };
            const num = parseFloat(processed);
            return (num >= 0 && num <= 100)
                ? { ...prev, ewDefectDeterminationFee: processed }
                : prev;
        },
    }), []);

    const validationSchema = useMemo(() => ({
        reportType: { required: true, message: 'Rapor türü gereklidir.' },
        arrivalDate: { required: true, message: 'Geliş tarihi gereklidir.' },
        lastDay: { required: true, message: 'Son gün gereklidir.' },
        ewDefectDeterminationFee: {
            required: true,
            message: 'Kusur tespit zorunludur.',
            min: 0,
            max: 100,
            rangeMessage: 'Kusur tespit yüzdesi 0-100 arasında olmalıdır.',
        },
        ewClaimDeterminationFee: {
            required: true,
            message: 'Talep tespit zorunludur.',
            custom: (value) => (parseCurrencyToFloat(value) < 0 ? 'Talep tespit negatif olamaz.' : null),
        },
        amendmentFee: {
            required: true,
            message: 'Islah ücreti zorunludur.',
            custom: (value) => (parseCurrencyToFloat(value) < 0 ? 'Islah ücreti negatif olamaz.' : null),
        },
        actionForReport: { required: true, message: 'Rapor için eylem zorunludur.' },
    }), []);

    const {
        formData: expertWitnessReportData,
        errors: validationErrors,
        handleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedExpertWitnessReport,
        initialData: initialExpertWitnessReportData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const commentText = selectedItem?.comments && Array.isArray(selectedItem.comments) && selectedItem.comments.length > 0
                ? selectedItem.comments[0].comment || ''
                : '';

            return {
                ...initialData,
                id: selectedItem?.id || '',
                arbitration: selectedItem?.arbitration || [arbitrationId],
                arrivalDate: selectedItem?.arrivalDate ? selectedItem.arrivalDate.split('T')[0] : '',
                lastDay: selectedItem?.lastDay ? selectedItem.lastDay.split('T')[0] : '',
                reportType: selectedItem?.reportType || '',
                ewDefectDeterminationFee: selectedItem?.ewDefectDeterminationFee?.toString() || '',
                ewClaimDeterminationFee: selectedItem?.ewClaimDeterminationFee?.toString() || '',
                actionForReport: selectedItem?.actionForReport || '',
                amendmentFee: selectedItem?.amendmentFee?.toString() || '',
                comments: commentText,
            };
        },
        normalizeInput: true,
        customHandlers,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            refreshData,
            componentName: 'Bilirkişi raporu',
            buildPayload: (data) => ({
                ...data,
                arbitration: [arbitrationId],
                ewDefectDeterminationFee: parseCurrencyToFloat(data.ewDefectDeterminationFee),
                ewClaimDeterminationFee: parseCurrencyToFloat(data.ewClaimDeterminationFee),
                amendmentFee: parseCurrencyToFloat(data.amendmentFee),
                comments: data.comments ? [{ comment: data.comments }] : [],
            }),
        },
    });

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'Bilirkişi Raporu Ekle' : 'Bilirkişi Raporu Düzenle'}
            </ModalHeader>
            <Form>
                <ModalBody>
                    {Object.keys(validationErrors).length > 0 && (
                        <Alert color="danger">
                            <ul className="mb-0">
                                {Object.values(validationErrors).filter(Boolean).map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arrivalDate">
                                    Geliş Tarihi <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="date"
                                    name="arrivalDate"
                                    id="arrivalDate"
                                    value={expertWitnessReportData.arrivalDate}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.arrivalDate}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="lastDay">Son Gün <span className="text-danger">*</span></Label>
                                <Input
                                    type="date"
                                    name="lastDay"
                                    id="lastDay"
                                    value={expertWitnessReportData.lastDay}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.lastDay}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label for="reportType">
                                    Rapor Türü <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="select"
                                    name="reportType"
                                    id="reportType"
                                    value={expertWitnessReportData.reportType}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.reportType}
                                >
                                    <option value="">Rapor türü seçiniz</option>
                                    {EXPERT_REPORT_TYPES.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label for="ewDefectDeterminationFee">Kusur Tespit <span className="text-danger">*</span></Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="ewDefectDeterminationFee"
                                        id="ewDefectDeterminationFee"
                                        value={displayDecimal(expertWitnessReportData.ewDefectDeterminationFee)}
                                        onChange={handleInputChange}
                                        placeholder="0-100"
                                        invalid={!!validationErrors.ewDefectDeterminationFee}
                                        style={{
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText
                                        style={{
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        %
                                    </InputGroupText>
                                </InputGroup>
                                {validationErrors.ewDefectDeterminationFee && (
                                    <small className="text-danger">{validationErrors.ewDefectDeterminationFee}</small>
                                )}
                                <small className="text-muted">0-100 arası tam sayı giriniz</small>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="ewClaimDeterminationFee">Talep Tespit <span className="text-danger">*</span></Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="ewClaimDeterminationFee"
                                        id="ewClaimDeterminationFee"
                                        value={displayDecimal(expertWitnessReportData.ewClaimDeterminationFee)}
                                        onChange={handleInputChange}
                                        placeholder="Örn: 1000,50"
                                        invalid={!!validationErrors.ewClaimDeterminationFee}
                                        style={{
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText
                                        style={{
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        ₺
                                    </InputGroupText>
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="amendmentFee">Islah Ücreti <span className="text-danger">*</span></Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="amendmentFee"
                                        id="amendmentFee"
                                        value={displayDecimal(expertWitnessReportData.amendmentFee)}
                                        onChange={handleInputChange}
                                        placeholder="Örn: 1000,50"
                                        invalid={!!validationErrors.amendmentFee}
                                        style={{
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText
                                        style={{
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        ₺
                                    </InputGroupText>
                                </InputGroup>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label for="actionForReport">Rapor İçin Eylem <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="actionForReport"
                                    id="actionForReport"
                                    value={expertWitnessReportData.actionForReport}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Eylem seçiniz</option>
                                    {ACTIONS_FOR_REPORT.map(action => (
                                        <option key={action} value={action}>{action}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label for="comments">Yorum</Label>
                                <Input
                                    type="textarea"
                                    name="comments"
                                    id="comments"
                                    rows="3"
                                    value={expertWitnessReportData.comments}
                                    onChange={handleInputChange}
                                    placeholder="Yorumlarınızı giriniz..."
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        İptal
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default ExpertWitnessReportDataModal;
